import {CSSProperties} from "react";
import {useApp} from "../app.context";
import {useIntegrator} from "../integrator.context";
import AssetImage from "./assetImage.element";
import {resolveUnlockImageKey} from "../features/techTree/techUnlock";
import {isAttributeCollection} from "../commands/metadataValue.command";
import {findMetadata} from "../utils/metadata.utils";
import {getUnitSources, isUnobtainable} from "../utils/unitSources";

const NO_BUILDING_TOOLTIP = 'No building produces this unit, so players can never recruit it. '
    + 'Drag it onto a building in the Buildings tab to add it to that building\'s Produceable Units.';
const ONLY_INACTIVE_TOOLTIP = 'Only inactive buildings produce this unit, so players can never recruit it.';
const GRANTED_TOOLTIP = 'No building produces this unit; it reaches the game through these instead.';

interface UnitSourcesProps {
    unitId: string;
    // Leads the building chips with "Produced in", for a header where they would
    // otherwise sit unexplained. The Tech Tree palette row has no room for it.
    labelled?: boolean;
    style?: CSSProperties;
}

// Where a unit comes from in game (see getUnitSources): the buildings that produce
// it, as chips that open the building in the Buildings tab the way an attribute's
// "Assigned to" links do. A unit nothing produces or grants shows a warning instead.
function UnitSources(props: UnitSourcesProps) {
    const {state} = useIntegrator();
    const {getLocalizationValue, navigateToBuilding, navigateTo} = useApp();

    const sources = getUnitSources(state, props.unitId);
    const byName = (a: string, b: string) => getLocalizationValue(a).localeCompare(getLocalizationValue(b));
    const buildings = [...sources.buildings].sort((a, b) => byName(a.id, b.id));
    const grants = [...sources.grants].sort((a, b) => byName(a.uid, b.uid));

    return (
        <div className={'row-links'} style={props.style}>
            {isUnobtainable(sources) && (
                <span className={'row-links-warning'} title={buildings.length === 0 ? NO_BUILDING_TOOLTIP : ONLY_INACTIVE_TOOLTIP}>
                    <span className={'fa-solid fa-triangle-exclamation'}/>
                    {buildings.length === 0 && ' No building'}
                </span>
            )}
            {props.labelled && buildings.length > 0 && <span className={'muted-text'}>Produced in</span>}
            {buildings.map(building => {
                const name = getLocalizationValue(building.id);
                return <SourceChip key={building.id} name={name}
                                   imageKey={resolveUnlockImageKey(state, building.id)}
                                   inactive={!building.active}
                                   title={building.active
                                       ? `Open ${name} in the Buildings tab`
                                       : `${name} is inactive, so it produces nothing in game. Click to open it in the Buildings tab.`}
                                   onClick={() => navigateToBuilding(building.id)}/>;
            })}
            {grants.length > 0 && <span className={'muted-text'} title={GRANTED_TOOLTIP}>Granted by</span>}
            {grants.map(meta => {
                const name = getLocalizationValue(meta.uid);
                // Attribute collections carry no icon (as in MetadataLabel).
                const found = findMetadata(state, meta.uid);
                const hasIcon = !found || !isAttributeCollection(found.collection);
                return <SourceChip key={meta.uid} name={name}
                                   imageKey={hasIcon ? meta.uid : undefined}
                                   title={`Open ${name}`}
                                   onClick={() => navigateTo({kind: 'metadata', uid: meta.uid})}/>;
            })}
        </div>
    );
}

// A button, not a link-styled span: a Ctrl/Cmd-click on it then reaches its own
// handler instead of the row's unit navigation (navTargetProps skips buttons), and
// a .dim-row-content row leaves it at full strength. The click stops here, so the
// row it sits in does not also open its description popup.
function SourceChip(props: { name: string, imageKey?: string, inactive?: boolean, title: string, onClick(): void }) {
    const className = ['entity-chip', props.imageKey ? 'with-icon' : '', props.inactive ? 'inactive' : '']
        .filter(c => c).join(' ');
    return (
        <button className={className} title={props.title} onClick={(e) => {
            e.stopPropagation();
            props.onClick();
        }}>
            {props.imageKey && <AssetImage id={props.imageKey} size={18} background={'transparent'}/>}
            <span>{props.name}</span>
        </button>
    );
}

export default UnitSources;
